<template>
  <v-row class="ma-0 pb-2">
    <v-col cols="12" class="pa-0">
      <v-sheet elevation="2" rounded class="px-6 py-4">
        <v-expansion-panels
          v-model="activePanel"
          accordion
          class="bordered"
          flat
        >
          <v-expansion-panel>
            <v-expansion-panel-header class="px-6 py-6">
              <p class="font-weight-medium mb-0 grey--text text--darken-3">
                {{ $t('common.rev_other') }}
              </p>
              <div
                v-if="activePanel === 0"
                class="d-flex justify-end align-center"
              >
                <img :src="ReverIcon" alt="rever_icon" class="mr-2" />
                <p class="ma-0 pa-0 caption grey--text text--darken-3">
                  {{ revTableFilterText }}
                </p>
                <Menu :closeOnContentClick="false" :options="revOptions">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="blue-grey"
                      small
                      v-on="on"
                      data-testid="profile-menu-activityFilter"
                    >
                      <v-icon>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                </Menu>
              </div>
            </v-expansion-panel-header>
            <v-divider></v-divider>
            <v-expansion-panel-content class="pt-2">
              <v-skeleton-loader
                v-if="isLoading"
                type="table-tbody, table-tfoot"
              ></v-skeleton-loader>

              <RevsTable
                :organizationId="organizationId"
                :accountId="accountId"
                :isSelfProfile="isSelfProfile"
                :filterSelected="revFilter"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel v-if="hasRoutinesFeatureEnabled">
            <v-expansion-panel-header class="px-6 py-6">
              <p class="font-weight-medium mb-0 grey--text text--darken-3">
                {{ $t('routines.routine_other') }}
              </p>
            </v-expansion-panel-header>
            <v-divider></v-divider>
            <v-expansion-panel-content class="pt-2">
              <v-skeleton-loader
                v-if="isLoading"
                type="table-tbody, table-tfoot"
              ></v-skeleton-loader>
              <RoutineTable :user="user" :organizationId="organizationId" />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel v-if="hasRoutinesFeatureEnabled">
            <v-expansion-panel-header class="px-6 py-6">
              <p class="font-weight-medium mb-0 grey--text text--darken-3">
                {{ $t('routines.finding_other') }}
              </p>

              <div
                v-if="activePanel === 2"
                class="d-flex justify-end align-center"
              >
                <img :src="ReverIcon" alt="rever_icon" class="mr-2" />
                <p class="ma-0 pa-0 caption grey--text text--darken-3">
                  {{ findingsTableFilterText }}
                </p>
                <Menu :closeOnContentClick="false" :options="findingsOptions">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="blue-grey"
                      small
                      v-on="on"
                      data-testid="profile-menu-activityFilter"
                    >
                      <v-icon>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                </Menu>
              </div>
            </v-expansion-panel-header>
            <v-divider></v-divider>
            <v-expansion-panel-content class="pt-2">
              <v-skeleton-loader
                v-if="isLoading"
                type="table-tbody, table-tfoot"
              ></v-skeleton-loader>

              <FindingsTable
                :organizationId="organizationId"
                :accountId="accountId"
                :isSelfProfile="isSelfProfile"
                :filterSelected="findingsFilter"
                @showFinding="showFinding"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-sheet>
    </v-col>

    <FindingDrawer
      v-if="isOpenFindingDrawer"
      :readOnly="!isSelfProfile"
      :visible="isOpenFindingDrawer"
      :findingId="selectedFindingId"
      @close="isOpenFindingDrawer = false"
    />
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex'

import RevsTable from './RevsSection.vue'
import RoutineTable from './RoutinesSection.vue'
import FindingsTable from './FindingsSection.vue'
import FindingDrawer from '@/components/FindingDrawer/FindingDrawer.vue'
import ReverIcon from '../../../assets/images/rever-filter-icon.svg'
import Menu from '@/components/UI/Menu.vue'

export default {
  name: 'UserRevs',

  components: {
    RevsTable,
    RoutineTable,
    FindingsTable,
    FindingDrawer,
    Menu,
  },

  props: {
    organizationId: {
      required: true,
      type: String,
    },
    user: {
      required: true,
      type: Object,
    },
    isSelfProfile: {
      required: true,
      type: Boolean,
    },
    isLoading: {
      required: true,
      type: Boolean,
    },
  },

  data() {
    return {
      activePanel: 0,
      revFilter: { text: this.$t('profile.user_revs'), value: 'owner' },
      findingsFilter: 'CREATED_BY_ME',
      isOpenFindingDrawer: false,
      selectedFindingId: '',
      ReverIcon,
    }
  },

  computed: {
    ...mapGetters(['organization']),

    hasRoutinesFeatureEnabled() {
      return Boolean(this.organization?.featuresEnabled__routines)
    },

    accountId() {
      return this.user?.id ?? ''
    },

    revTableFilterText() {
      return this.revFilter.text
    },

    findingsTableFilterText() {
      const selectedOption = this.findingsOptions.find(
        option => option.value === this.findingsFilter
      )

      return selectedOption.text
    },

    revOptions() {
      return [
        {
          title: this.$t('profile.user_revs'),
          value: 'owner',
          action: () =>
            this.filterRev({
              text: this.$t('profile.user_revs'),
              value: 'owner',
            }),
        },
        {
          title: this.$t('profile.collaborating'),
          value: 'collaborators',
          action: () =>
            this.filterRev({
              text: this.$t('profile.collaborating'),
              value: 'collaborators',
            }),
        },
        {
          title: this.$t('common.coaching'),
          value: 'coach',
          action: () =>
            this.filterRev({
              text: this.$t('common.coaching'),
              value: 'coach',
            }),
        },
      ]
    },

    userName() {
      return this.user?.nickname ?? ''
    },

    findingsOptions() {
      return [
        {
          title: this.isSelfProfile
            ? this.$t('routines.findings_assigned_to_me')
            : this.$t('routines.findings_assigned_to_user', {
                name: this.userName,
              }),
          value: 'ASSIGNED_TO_ME',
          action: () => this.filterFinding('ASSIGNED_TO_ME'),
        },
        {
          title: this.isSelfProfile
            ? this.$t('routines.findings_created_by_me')
            : this.$t('routines.findings_created_by_user', {
                name: this.userName,
              }),
          value: 'CREATED_BY_ME',
          action: () => this.filterFinding('CREATED_BY_ME'),
        },
      ]
    },
  },

  methods: {
    filterFinding(filter) {
      this.findingsFilter = filter
    },

    filterRev(filter) {
      this.revFilter = filter
    },

    showFinding(findingId) {
      this.selectedFindingId = findingId
      this.isOpenFindingDrawer = true
    },
  },
}
</script>
<style lang="scss">
.v-expansion-panel-content__wrap {
  padding: 0 !important;
}
.bordered {
  border: 1px solid $color-blue-grey-lighten2;
}
</style>
